import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  CircularProgress,
  IconButton,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  InputAdornment,
} from "@material-ui/core";
import {
  Warning as WarningIcon,
  SwapHoriz as SwapHorizIcon,
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Language as LanguageIcon,
  Category as CategoryIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    "& .MuiDialog-paper": {
      maxWidth: "680px",
      width: "95%",
    },
  },
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2, 3),
  },
  titleContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  warningBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(3, 2),
    textAlign: "center",
  },
  warningIcon: {
    fontSize: 56,
    color: theme.palette.warning.main,
  },
  warningTitle: {
    fontWeight: 700,
    fontSize: "1.2rem",
  },
  warningSubtitle: {
    fontSize: "0.9rem",
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    maxWidth: 460,
  },
  connectionList: {
    maxHeight: 320,
    overflowY: "auto",
  },
  connectionItem: {
    cursor: "pointer",
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(0.5),
    border: `1px solid transparent`,
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      border: `1px solid ${theme.palette.primary.light}`,
    },
  },
  connectionItemSelected: {
    backgroundColor: theme.palette.primary.light + "20",
    border: `1px solid ${theme.palette.primary.main}`,
  },
  connectionAvatar: {
    backgroundColor: theme.palette.success.main,
  },
  connectionName: {
    fontWeight: 600,
  },
  stepLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: theme.palette.primary.main,
    textTransform: "uppercase",
    letterSpacing: 1,
    marginBottom: theme.spacing(1.5),
  },
  // Template styles
  searchBox: {
    marginBottom: theme.spacing(2),
  },
  templateGrid: {
    maxHeight: 350,
    overflowY: "auto",
    paddingRight: theme.spacing(0.5),
  },
  templateCard: {
    cursor: "pointer",
    border: `1px solid ${theme.palette.divider}`,
    transition: "all 0.2s",
    "&:hover": {
      border: `1px solid ${theme.palette.primary.main}`,
      boxShadow: theme.shadows[4],
    },
  },
  templatePreview: {
    backgroundColor: theme.palette.action.hover,
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(0.5),
    fontSize: "0.8rem",
    whiteSpace: "pre-wrap",
    minHeight: 60,
    maxHeight: 100,
    overflowY: "auto",
  },
  templateDetailBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
  },
  variableSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  stepIndicator: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "center",
    marginBottom: theme.spacing(2),
  },
  stepDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: theme.palette.action.disabled,
    transition: "background-color 0.3s",
  },
  stepDotActive: {
    backgroundColor: theme.palette.primary.main,
    width: 20,
    borderRadius: 4,
  },
}));

// Step constants
const STEP_CONFIRM = 0;
const STEP_SELECT_CONNECTION = 1;
const STEP_SELECT_TEMPLATE = 2;

const SwitchChannelOficialModal = ({ open, onClose, ticketId, onSwitched }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [step, setStep] = useState(STEP_CONFIRM);
  const [connections, setConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variables, setVariables] = useState({});
  const [variableValues, setVariableValues] = useState({});
  const [renderedContent, setRenderedContent] = useState("");

  const [submitting, setSubmitting] = useState(false);

  // Reset modal state when opened
  useEffect(() => {
    if (open) {
      setStep(STEP_CONFIRM);
      setSelectedConnection(null);
      setSelectedTemplate(null);
      setVariables({});
      setVariableValues({});
      setRenderedContent("");
      setTemplateSearch("");
    }
  }, [open]);

  // Fetch official connections when moving to step 1
  useEffect(() => {
    if (step !== STEP_SELECT_CONNECTION) return;

    setLoadingConnections(true);
    api
      .get("/whatsapp/?session=0")
      .then(({ data }) => {
        const oficiais = data.filter(
          (w) => w.channel === "whatsapp_oficial"
        );
        setConnections(oficiais);
      })
      .catch((err) => toastError(err))
      .finally(() => setLoadingConnections(false));
  }, [step]);

  // Fetch templates when a connection is selected
  useEffect(() => {
    if (!selectedConnection) return;

    setLoadingTemplates(true);
    setSelectedTemplate(null);
    setVariables({});
    setVariableValues({});

    api
      .request({
        url: "/quick-messages/list",
        method: "GET",
        params: {
          isOficial: "true",
          status: "APPROVED",
          whatsappId: selectedConnection.id,
          companyId: user.companyId,
          userId: user.id,
        },
      })
      .then(({ data }) => setTemplates(data))
      .catch((err) => toastError(err))
      .finally(() => setLoadingTemplates(false));
  }, [selectedConnection]);

  // ---- Template variable helpers ----
  const extractVariablesByComponent = (components) => {
    const regex = /{{(\d+)}}/g;
    const vars = { header: [], body: [], footer: [], buttons: [] };
    components.forEach((comp) => {
      let match;
      const type = comp.type?.toLowerCase();
      const text = comp.text || "";
      if (type === "buttons") {
        const buttons = JSON.parse(comp.buttons);
        buttons.forEach((button, index) => {
          if (button.example) {
            vars[type].push({ type: button.type, prompt: button.example, index });
          }
        });
      } else if (vars[type] !== undefined) {
        while ((match = regex.exec(text)) !== null) {
          vars[type].push({ type: "text", prompt: match[0] });
        }
      }
    });
    return vars;
  };

  const handleSelectTemplate = (template) => {
    const comps = template?.components || [];
    const extracted = extractVariablesByComponent(comps);
    setSelectedTemplate(template);
    setVariables(extracted);
    setVariableValues({});
    setRenderedContent(comps.map((c) => c?.text).join("\n"));
  };

  const handleVariableChange = (componentType, index, value, buttonIndex) => {
    setVariableValues((prev) => ({
      ...prev,
      [componentType]: {
        ...prev[componentType],
        [index]: { value, buttonIndex },
      },
    }));
  };

  const generateBodyToSave = (content, vars) => {
    let body = content;
    Object.keys(vars).forEach((compType) => {
      const compVars = vars[compType];
      if (Array.isArray(compVars)) {
        compVars.forEach((variable, idx) => {
          const placeholder = `{{${idx + 1}}}`;
          const val = variable?.value || "";
          body = body.replace(placeholder, val);
        });
      }
    });
    return body;
  };

  const getTemplatePreview = (components) => {
    if (!components || components.length === 0) return "Sem preview disponível";
    return components
      .filter((c) => c?.text)
      .map((c) => c.text)
      .join("\n\n");
  };

  const filteredTemplates = templates.filter((t) =>
    t?.shortcode?.toLowerCase().includes(templateSearch.toLowerCase())
  );

  // ---- Submit ----
  const handleSubmit = async () => {
    if (!selectedConnection || !selectedTemplate) return;

    setSubmitting(true);
    try {
      const bodyToSave = generateBodyToSave(renderedContent, variableValues);

      await api.post(`/tickets/${ticketId}/switch-channel-oficial`, {
        newWhatsappId: selectedConnection.id,
        templateId: selectedTemplate.id,
        variables: variableValues,
        bodyToSave,
      });

      if (onSwitched) onSwitched();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Step dots ----
  const renderStepDots = () => (
    <Box className={classes.stepIndicator}>
      {[STEP_CONFIRM, STEP_SELECT_CONNECTION, STEP_SELECT_TEMPLATE].map((s) => (
        <Box
          key={s}
          className={`${classes.stepDot} ${step === s ? classes.stepDotActive : ""}`}
        />
      ))}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialogPaper} fullWidth maxWidth="md">
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box className={classes.titleContent}>
          <SwapHorizIcon color="primary" />
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            {step === STEP_CONFIRM && "Número Desconectado"}
            {step === STEP_SELECT_CONNECTION && "Selecionar Conexão Oficial"}
            {step === STEP_SELECT_TEMPLATE && "Selecionar Template"}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {renderStepDots()}

        {/* ---- STEP 0: CONFIRM ---- */}
        {step === STEP_CONFIRM && (
          <Box className={classes.warningBox}>
            <WarningIcon className={classes.warningIcon} />
            <Typography variant="h6" className={classes.warningTitle}>
              Número desconectado, deseja trocar?
            </Typography>
            <Typography variant="body2" className={classes.warningSubtitle}>
              A conversa será iniciada a partir de <strong>outro número (WhatsApp Oficial)</strong>,
              exigindo o envio de um <strong>TEMPLATE</strong> para iniciar a nova conversa.
            </Typography>
          </Box>
        )}

        {/* ---- STEP 1: SELECT CONNECTION ---- */}
        {step === STEP_SELECT_CONNECTION && (
          <Box mt={1}>
            <Typography className={classes.stepLabel}>
              Conexões WhatsApp Oficial disponíveis
            </Typography>

            {loadingConnections ? (
              <Box className={classes.loadingBox}>
                <CircularProgress />
              </Box>
            ) : connections.length === 0 ? (
              <Box className={classes.emptyState}>
                <Typography>Nenhuma conexão WhatsApp Oficial encontrada.</Typography>
              </Box>
            ) : (
              <List className={classes.connectionList} disablePadding>
                {connections.map((conn) => (
                  <ListItem
                    key={conn.id}
                    className={`${classes.connectionItem} ${
                      selectedConnection?.id === conn.id ? classes.connectionItemSelected : ""
                    }`}
                    onClick={() => setSelectedConnection(conn)}
                  >
                    <ListItemAvatar>
                      <Avatar className={classes.connectionAvatar}>
                        {selectedConnection?.id === conn.id ? (
                          <CheckCircleIcon />
                        ) : (
                          conn.name?.[0]?.toUpperCase() || "W"
                        )}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={<span className={classes.connectionName}>{conn.name}</span>}
                      secondary={`Status: ${conn.status || "—"}  |  Canal: ${conn.channel}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}

        {/* ---- STEP 2: SELECT TEMPLATE ---- */}
        {step === STEP_SELECT_TEMPLATE && (
          <Box mt={1}>
            {!selectedTemplate ? (
              <>
                <Typography className={classes.stepLabel}>
                  Templates disponíveis — {selectedConnection?.name}
                </Typography>

                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Pesquisar template..."
                  value={templateSearch}
                  onChange={(e) => setTemplateSearch(e.target.value)}
                  className={classes.searchBox}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                {loadingTemplates ? (
                  <Box className={classes.loadingBox}>
                    <CircularProgress />
                  </Box>
                ) : filteredTemplates.length === 0 ? (
                  <Box className={classes.emptyState}>
                    <Typography>Nenhum template encontrado.</Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2} className={classes.templateGrid}>
                    {filteredTemplates.map((tpl, idx) => (
                      <Grid item xs={12} sm={6} key={idx}>
                        <Card className={classes.templateCard}>
                          <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                                {tpl.shortcode}
                              </Typography>
                              <Chip
                                label={tpl.status || "APPROVED"}
                                size="small"
                                color="primary"
                                style={{ fontSize: "0.65rem" }}
                              />
                            </Box>

                            <Box className={classes.templatePreview}>
                              {getTemplatePreview(tpl.components)}
                            </Box>

                            <Box display="flex" gap={8} flexWrap="wrap" mt={1}>
                              <Chip
                                icon={<CategoryIcon style={{ fontSize: "0.85rem" }} />}
                                label={tpl.category || "UTILITY"}
                                size="small"
                                color="default"
                                style={{ fontSize: "0.7rem" }}
                              />
                              <Chip
                                icon={<LanguageIcon style={{ fontSize: "0.85rem" }} />}
                                label={tpl.language || "pt_BR"}
                                size="small"
                                variant="outlined"
                                style={{ fontSize: "0.7rem" }}
                              />
                            </Box>
                          </CardContent>
                          <CardActions style={{ borderTop: "1px solid #f0f0f0", padding: "8px 16px" }}>
                            <Button
                              variant="contained"
                              color="primary"
                              fullWidth
                              size="small"
                              onClick={() => handleSelectTemplate(tpl)}
                              startIcon={<SendIcon />}
                            >
                              Usar Template
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            ) : (
              /* Template detail + variable fill */
              <Box className={classes.templateDetailBox}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                    {selectedTemplate.shortcode}
                  </Typography>
                  <Chip
                    label={selectedTemplate.status || "APPROVED"}
                    size="small"
                    color="primary"
                  />
                </Box>

                <Divider style={{ margin: "8px 0 12px" }} />

                <Box className={classes.templatePreview} style={{ minHeight: "auto" }}>
                  {renderedContent}
                </Box>

                <Box display="flex" gap={8} mt={1} mb={1} flexWrap="wrap">
                  <Chip
                    icon={<CategoryIcon />}
                    label={selectedTemplate.category || "UTILITY"}
                    size="small"
                  />
                  <Chip
                    icon={<LanguageIcon />}
                    label={selectedTemplate.language || "pt_BR"}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                {/* Variable inputs per component type */}
                {Object.keys(variables).map((componentType) => {
                  const compVars = variables[componentType];
                  if (!compVars || compVars.length === 0) return null;
                  return (
                    <Box key={componentType} className={classes.variableSection}>
                      <Typography
                        variant="caption"
                        style={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}
                      >
                        {componentType}
                      </Typography>
                      {compVars.map((variable, idx) => (
                        <TextField
                          key={`${componentType}-${idx}`}
                          label={variable?.prompt}
                          value={variableValues[componentType]?.[idx]?.value || ""}
                          onChange={(e) =>
                            handleVariableChange(
                              componentType,
                              idx,
                              e.target.value,
                              variable?.index || 0
                            )
                          }
                          fullWidth
                          variant="outlined"
                          size="small"
                          margin="dense"
                        />
                      ))}
                    </Box>
                  );
                })}

                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setSelectedTemplate(null);
                    setVariableValues({});
                    setRenderedContent("");
                  }}
                  startIcon={<ArrowBackIcon />}
                  style={{ marginTop: 12 }}
                >
                  Voltar para Templates
                </Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions style={{ padding: "12px 24px", justifyContent: "space-between" }}>
        {/* Left: back button */}
        <Box>
          {step > STEP_CONFIRM && (
            <Button
              onClick={() => {
                if (step === STEP_SELECT_TEMPLATE) setStep(STEP_SELECT_CONNECTION);
                else if (step === STEP_SELECT_CONNECTION) setStep(STEP_CONFIRM);
              }}
              startIcon={<ArrowBackIcon />}
            >
              Voltar
            </Button>
          )}
        </Box>

        {/* Right: action buttons */}
        <Box display="flex" gap={8}>
          <Button onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>

          {step === STEP_CONFIRM && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setStep(STEP_SELECT_CONNECTION)}
            >
              Sim, trocar de canal
            </Button>
          )}

          {step === STEP_SELECT_CONNECTION && (
            <Button
              variant="contained"
              color="primary"
              disabled={!selectedConnection}
              onClick={() => setStep(STEP_SELECT_TEMPLATE)}
            >
              Próximo: Selecionar Template
            </Button>
          )}

          {step === STEP_SELECT_TEMPLATE && selectedTemplate && (
            <ButtonWithSpinner
              variant="contained"
              color="primary"
              loading={submitting}
              disabled={submitting}
              onClick={handleSubmit}
              startIcon={<SendIcon />}
            >
              Enviar Template e Trocar Canal
            </ButtonWithSpinner>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SwitchChannelOficialModal;
